import {
	faEye,
	faFloppyDisk,
	faInfoCircle,
	faPlus,
	faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, {
	ChangeEvent,
	DragEvent,
	Fragment,
	ReactNode,
	Ref,
	SyntheticEvent,
	forwardRef,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { useDrag } from "../../../../../common/useDrag";
import { zip } from "../../../../../common/zip";
import { Autocomplete } from "../../../../editor/components/autocomplete/Autocomplete";
import { Coordinates } from "../../../../editor/components/coordinates/Coordinates";
import Tooltip from "../../../../editor/components/tooltip/Tooltip";
import {
	selectMemories,
	updateBlockDataValue,
	updateBlockVariableContent,
	updateMemoryRefSource,
	updateOperatorEnabledIos,
} from "../../../../editor/editorSlice";
import Memory from "../../../../editor/interfaces/components/Memory";
import Operator from "../../../../editor/interfaces/components/Operator";
import {
	Types,
	VariableContent,
} from "../../../../editor/interfaces/components/Variable";
import { LogicsMemory } from "../../../../logics/logics/LogicsMemory";
import { LogicsOperator } from "../../../../logics/logics/LogicsOperator";
import {
	LogicsData as DataConfig,
	LogicsTypes as TypesConfig,
	LogicsVariable as VariableConfig,
} from "../../../../logics/logics/LogicsVariable";
import { selectLanguages, selectLogics } from "../../../../logics/logicsSlice";
import {
	WindowTypeAndPropsDefinition,
	closeWindow,
	createWindow,
	resizeWindowByOffset,
	selectWindow,
} from "../../../windowManagerSlice";
import css from "./BlockConfigWindow.module.css";

const DEVICE_WIDTH = 480;
const DEVICE_HEIGHT = 160;
const IMAGE_MAX_SIZE = 2048;

interface Saveable {
	saveVariables: () => void;
}

interface BlockSourceOperator {
	type: "operator";
	operatorId: string;
	sheetIndex: number;
}

interface BlockSourceMemory {
	type: "memoryRef";
	memoryRefId: string;
	sheetIndex: number;
}

export type BlockSource = BlockSourceOperator | BlockSourceMemory;

export interface BlockConfigWindowProps {
	source: BlockSource;
}

export type BlockConfigWindowTypeAndProps = WindowTypeAndPropsDefinition<
	"blockConfig",
	BlockConfigWindowProps
>;

export interface ImageValues {
	start_x?: number;
	start_y?: number;
	end_x?: number;
	end_y?: number;
	size?: number;
	data?: string;
}

interface DataFieldProps {
	content: string | string[];
	config: DataConfig;
	onChange: (value: string) => void;
	index: number;
	label: string | ReactNode;
	tooltipText: string;
	onBlur?: (value: string, multiple: boolean) => void;
	maximumSize?: number;
	disabled?: boolean;
	iconLanguageIndex?: ReactNode | null;
}

interface DataImageFieldProps {
	content: string | string[];
	onChange: (value: ImageValues) => void;
	index: number;
	tooltipText: string;
	onBlur?: (value: string, multiple: boolean) => void;
	maximumSize?: number;
	disabled?: boolean;
	block: Operator | Memory;
	message?: string;
	config: LogicsOperator | LogicsMemory;
}

const fromHex = (hex: string | string[]): Uint8Array => {
	const pairs = typeof hex === "string" ? (hex.match(/.{1,2}/g) ?? []) : hex;

	return Uint8Array.from(pairs!.map((byte) => parseInt(byte, 16)));
};

function OperatorConfigWindow(props: BlockSourceOperator & { id: string }) {
	const dispatch = useAppDispatch();
	const logics = useAppSelector(selectLogics);
	const operator = useAppSelector((state) =>
		state.editor.sheets[props.sheetIndex].operators.find(
			(operator) => operator.id === props.operatorId,
		),
	);
	const window = useAppSelector(selectWindow(props.id));

	const [canSaveVariables, setCanSaveVariables] = useState(false);
	const [canSaveData, setCanSaveData] = useState(false);
	const [canSaveIoEnables, setCanSaveIoEnables] = useState(false);

	const [hasVariablesChange, setHasVariablesChange] = useState(false);
	const [hasDataChange, setHasDataChange] = useState(false);
	const [hasIoEnablesChange, setHasIoEnablesChange] = useState(false);

	const variablesRef = useRef<Saveable>(null);
	const dataRef = useRef<Saveable>(null);
	const ioEnablesRef = useRef<Saveable>(null);

	const contentRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!contentRef.current) {
			return;
		}

		if (contentRef.current.scrollHeight > window.size.y) {
			dispatch(
				resizeWindowByOffset({
					windowId: props.id,
					delta: {
						x: 0,
						y: contentRef.current.scrollHeight - window.size.y + 16,
					},
				}),
			);
		}
	}, []);

	if (!operator) {
		dispatch(closeWindow(props.id));
		return null;
	}

	const config = logics.operator[operator.name];

	const canSave = canSaveVariables && canSaveData && canSaveIoEnables;
	const hasChange = hasVariablesChange || hasDataChange || hasIoEnablesChange;

	return (
		<div className={css.content} ref={contentRef}>
			<BlockConfigWindowVariables
				ref={variablesRef}
				source={props}
				block={operator}
				config={config}
				setCanSave={setCanSaveVariables}
				setHasChange={setHasVariablesChange}
			/>
			<BlockConfigWindowData
				ref={dataRef}
				source={props}
				block={operator}
				config={config}
				setCanSave={setCanSaveData}
				setHasChange={setHasDataChange}
			/>
			<BlockConfigWindowIoEnables
				ref={ioEnablesRef}
				source={props}
				operator={operator}
				logicsOperator={config}
				setCanSave={setCanSaveIoEnables}
				setHasChange={setHasIoEnablesChange}
			/>
			<button
				className={css.saveButton}
				disabled={!(canSave && hasChange)}
				onClick={() => {
					variablesRef.current?.saveVariables();
					dataRef.current?.saveVariables();
					ioEnablesRef.current?.saveVariables();
				}}
				style={{
					position: "absolute",
					right: 55,
					top: 2,
				}}
			>
				<FontAwesomeIcon icon={faFloppyDisk} />
			</button>
		</div>
	);
}

function MemoryRefConfigWindow(props: BlockSourceMemory & { id: string }) {
	const dispatch = useAppDispatch();
	const { t } = useTranslation();

	const logics = useAppSelector(selectLogics);
	const languages = useAppSelector(selectLanguages);
	const memories = useAppSelector(selectMemories);
	const memoryRef = useAppSelector((state) =>
		state.editor.sheets[props.sheetIndex].memoryRefs.find(
			(memoryRef) => memoryRef.id === props.memoryRefId,
		),
	);

	const [canSaveVariables, setCanSaveVariables] = useState(false);
	const [canSaveData, setCanSaveData] = useState(false);
	const [hasVariablesChange, setHasVariablesChange] = useState(false);
	const [hasDataChange, setHasDataChange] = useState(false);

	const variablesRef = useRef<Saveable>(null);
	const dataRef = useRef<Saveable>(null);

	if (!memoryRef) {
		dispatch(closeWindow(props.id));
		return null;
	}

	const config = logics.memory[memoryRef.name];

	const memory = useAppSelector((state) =>
		state.editor.memories[memoryRef.name].find(
			(memory) => memory.id === memoryRef.sourceMemoryId,
		),
	);

	const canSave = canSaveVariables && canSaveData;
	const hasChange = hasVariablesChange || hasDataChange;

	return (
		<div className={css.content}>
			<div className={css.row}>
				<div className={css.sectionTitle}>Memory</div>
				<Autocomplete
					value={memory?.label ?? ""}
					options={memories[config.name].map((item) => item.label)}
					onChange={(value) => {
						const associatedMemory = memories[memoryRef.name].find(
							(memory) => memory.label === value,
						);
						if (associatedMemory) {
							dispatch(
								updateMemoryRefSource({
									sheetId: memoryRef.sheetId,
									memoryRefId: memoryRef.id,
									sourceMemoryId: associatedMemory.id,
								}),
							);
						}
					}}
				/>
			</div>

			<button
				className={css.saveButton}
				onClick={() => {
					const windowSize = {
						x: 800,
						y: 500,
					};

					const windowPosition = {
						x: (window.innerWidth - windowSize.x) / 2,
						y: (window.innerHeight - windowSize.y) / 2,
					};

					dispatch(
						createWindow({
							title: t("Memory Manager"),
							typeAndProps: {
								type: "memoryManager",
								props: {
									initialCategory: memoryRef.name,
									linkToMemoryRef: {
										sheetId: memoryRef.sheetId,
										memoryRefId: memoryRef.id,
									},
								},
							},
							position: windowPosition,
							size: windowSize,
						}),
					);
				}}
				style={{
					position: "absolute",
					right: 85,
					top: 2,
				}}
			>
				<FontAwesomeIcon icon={faPlus} />
			</button>
			<button
				className={css.saveButton}
				disabled={!(canSave && hasChange)}
				onClick={() => {
					variablesRef.current?.saveVariables();
					dataRef.current?.saveVariables();
				}}
				style={{
					position: "absolute",
					right: 55,
					top: 2,
				}}
			>
				<FontAwesomeIcon icon={faFloppyDisk} />
			</button>

			{memory && (
				<React.Fragment key={memory.label}>
					<BlockConfigWindowVariables
						ref={variablesRef}
						source={props}
						block={memory}
						config={config}
						setCanSave={setCanSaveVariables}
						setHasChange={setHasVariablesChange}
					/>
					<BlockConfigWindowData
						ref={dataRef}
						source={props}
						block={memory}
						config={config}
						setCanSave={setCanSaveData}
						setHasChange={setHasDataChange}
						languages={languages}
					/>
				</React.Fragment>
			)}
		</div>
	);
}

const BlockConfigWindowVariables = forwardRef(
	(
		props: {
			source: BlockSource;
			block: Operator | Memory;
			config: LogicsOperator | LogicsMemory;
			setCanSave: (canSave: boolean) => void;
			setHasChange: (hasChange: boolean) => void;
		},
		ref: Ref<Saveable>,
	) => {
		const { source, block, config } = props;
		const { i18n } = useTranslation();

		const dispatch = useAppDispatch();

		const nonPrivateVariables = config.variables.filter(
			(variable) => !variable.private,
		);
		const [variables, setVariables] = useState(block.metadata.variables);
		const setVariableContent = (name: string) => {
			return (content: VariableContent) =>
				setVariables((variables) => {
					return {
						...variables,
						[name]: {
							...variables[name],
							content,
						},
					};
				});
		};

		const hasVariableChanges = useMemo(() => {
			return Object.entries(variables).some(([name, variable]) => {
				const originalJson = JSON.stringify(
					block.metadata.variables[name].content,
				);
				const newJson = JSON.stringify(variable.content);
				return originalJson !== newJson;
			});
		}, [variables]);

		const areVariablesValid = useMemo(() => {
			return Object.entries(variables).every(([_name, variable]) => {
				switch (variable.content.type) {
					case "boolean":
					case "enum":
						return true;
					case "number":
						return !isNaN((variable.content as Types.Number).value);
				}
			});
		}, [variables]);

		useEffect(() => {
			props.setCanSave(areVariablesValid);
			props.setHasChange(hasVariableChanges);
		}, [hasVariableChanges, areVariablesValid]);

		useImperativeHandle(
			ref,
			() => ({
				saveVariables: () => {
					Object.entries(variables)
						.filter(([name, _]) =>
							nonPrivateVariables.find((priv) => priv.name == name),
						)
						.forEach(([name, variable]) => {
							dispatch(
								updateBlockVariableContent({
									source,
									variableName: name,
									content: variable.content,
								}),
							);
							setVariableContent(name)(variable.content);
						});
				},
			}),
			[variables],
		);

		if (nonPrivateVariables.length === 0) {
			return null;
		}

		return (
			<>
				<div className={css.row}>
					<div className={css.sectionTitle}>Variables</div>
					<div className={css.sectionTitle}></div>
				</div>
				{nonPrivateVariables.map((config, index) => {
					const variable = variables[config.name];

					const tooltipText =
						typeof config.description == "string"
							? config.description
							: config.description[i18n.language] || config.description["en"];

					return (
						<div key={`tooltip-item-${index}`} className={css.row}>
							<div className={css.label}>
								{variable.label}
								<Tooltip text={tooltipText}>
									<FontAwesomeIcon
										color="#DC7609"
										size="xs"
										icon={faInfoCircle}
									/>
								</Tooltip>
							</div>

							<React.Fragment key={index}>
								{variable.content.type === "boolean" && (
									<BooleanVariable
										content={variable.content}
										config={config}
										onChange={setVariableContent(config.name)}
									/>
								)}
								{variable.content.type === "enum" && (
									<EnumVariable
										content={variable.content}
										config={config}
										onChange={setVariableContent(config.name)}
									/>
								)}
								{variable.content.type === "number" && (
									<NumberVariable
										content={variable.content}
										config={config}
										onChange={setVariableContent(config.name)}
									/>
								)}
							</React.Fragment>
						</div>
					);
				})}
			</>
		);
	},
);

const BlockConfigWindowIoEnables = forwardRef(
	(
		props: {
			source: BlockSource;
			operator: Operator;
			logicsOperator: LogicsOperator;
			setCanSave: (canSave: boolean) => void;
			setHasChange: (hasChange: boolean) => void;
		},
		ref: Ref<Saveable>,
	) => {
		const { source, operator, logicsOperator } = props;

		const dispatch = useAppDispatch();

		const inputs = operator.inputs;
		const logicsInputs = logicsOperator.inputs;
		const nonRequiredInputs = logicsInputs.filter((input) => !input.required);

		const [inputEnables, setInputEnables] = useState(() =>
			Object.fromEntries(inputs.map((io) => [io.name, io.enabled])),
		);

		const outputs = operator.outputs;
		const logicsOutputs = logicsOperator.outputs;
		const nonRequiredOutputs = logicsOutputs.filter(
			(output) => !output.required,
		);

		const [outputEnables, setOutputEnables] = useState(() =>
			Object.fromEntries(outputs.map((io) => [io.name, io.enabled])),
		);

		const setEnable = (name: string, ioType: "input" | "output") => {
			return (enabled: Types.Boolean) => {
				switch (ioType) {
					case "input":
						setInputEnables((enables) => {
							return {
								...enables,
								[name]: enabled.value,
							};
						});
						break;

					case "output":
						setOutputEnables((enables) => {
							return {
								...enables,
								[name]: enabled.value,
							};
						});
						break;
				}
			};
		};

		const hasVariableChanges = useMemo(() => {
			return zip(
				Object.values(inputEnables),
				inputs.map((io) => io.enabled),
			)
				.concat(
					zip(
						Object.values(outputEnables),
						outputs.map((io) => io.enabled),
					),
				)
				.some(([a, b]) => a !== b);
		}, [inputEnables, outputEnables, props]);

		useEffect(() => {
			props.setCanSave(true);
			props.setHasChange(hasVariableChanges);
		}, [hasVariableChanges]);

		useImperativeHandle(
			ref,
			() => ({
				saveVariables: () => {
					Object.entries(inputEnables).forEach(([name, enabled]) => {
						dispatch(
							updateOperatorEnabledIos({
								sheetIndex: source.sheetIndex,
								operatorId: operator.id,
								ioType: "input",
								ioName: name,
								enabled,
							}),
						);
					});
					Object.entries(outputEnables).forEach(([name, enabled]) => {
						dispatch(
							updateOperatorEnabledIos({
								sheetIndex: source.sheetIndex,
								operatorId: operator.id,
								ioType: "output",
								ioName: name,
								enabled,
							}),
						);
					});
				},
			}),
			[inputEnables, outputEnables, props],
		);

		if (nonRequiredInputs.length === 0 && nonRequiredOutputs.length === 0) {
			return null;
		}

		return (
			<>
				<div className={css.row}>
					<div className={css.sectionTitle}>IO group configuration</div>
					<div className={css.sectionTitle}></div>
				</div>
				{nonRequiredInputs.map((logicsInput) => {
					const enabled = inputEnables[logicsInput.label];

					return (
						<div key={logicsInput.label} className={css.row}>
							<div className={css.label}>Enable {logicsInput.label}</div>
							<BooleanVariable
								content={{
									type: "boolean",
									value: enabled,
								}}
								onChange={setEnable(logicsInput.label, "input")}
							/>
						</div>
					);
				})}
				{nonRequiredOutputs.map((logicsOutput) => {
					const enabled = outputEnables[logicsOutput.label];

					return (
						<div key={logicsOutput.label} className={css.row}>
							<div className={css.label}>Enable {logicsOutput.label}</div>
							<BooleanVariable
								content={{
									type: "boolean",
									value: enabled,
								}}
								onChange={setEnable(logicsOutput.label, "output")}
							/>
						</div>
					);
				})}
			</>
		);
	},
);

const BlockConfigWindowData = forwardRef(
	(
		props: {
			source: BlockSource;
			block: Operator | Memory;
			config: LogicsOperator | LogicsMemory;
			setCanSave: (canSave: boolean) => void;
			setHasChange: (hasChange: boolean) => void;
			languages?: Array<string>;
		},
		ref: Ref<Saveable>,
	) => {
		const { source, block, config, languages } = props;

		const { i18n } = useTranslation();
		const dispatch = useAppDispatch();
		const [languageIndex, setLanguageIndex] = useState<number>(0);
		const hasImage = config.data?.find((data) => data.type == "file");

		const [data, setData] = useState<Record<string, string | Array<string>>>(
			block.metadata.data,
		);

		const setDataContent = (name: string) => {
			return (content: string) =>
				setData((data) => {
					return {
						...data,
						[name]: content,
					};
				});
		};

		const setDataImageContent = (name: string) => {
			return (content: ImageValues) => {
				const { start_x, start_y, end_x, end_y, size } = content;

				if (content?.data != undefined) {
					setData((data) => {
						return {
							...data,
							[name]: content?.data ?? "",
						};
					});
				}

				if (start_x != undefined) {
					dispatch(
						updateBlockVariableContent({
							source,
							variableName: "start_x",
							content: {
								type: "number",
								value: start_x,
							},
						}),
					);
				}

				if (start_y != undefined) {
					dispatch(
						updateBlockVariableContent({
							source,
							variableName: "start_y",
							content: {
								type: "number",
								value: start_y,
							},
						}),
					);
				}

				if (end_x != undefined) {
					dispatch(
						updateBlockVariableContent({
							source,
							variableName: "end_x",
							content: {
								type: "number",
								value: end_x,
							},
						}),
					);
				}

				if (end_y != undefined) {
					dispatch(
						updateBlockVariableContent({
							source,
							variableName: "end_y",
							content: {
								type: "number",
								value: end_y,
							},
						}),
					);
				}

				if (size != undefined) {
					dispatch(
						updateBlockVariableContent({
							source,
							variableName: name,
							content: {
								type: "number",
								value: size,
							},
						}),
					);
				}
			};
		};

		const setDataParentContent = (name: string, index: number) => {
			return (content: string) =>
				setData((data) => {
					const oldData = typeof data[name] == "string" ? [] : [...data[name]];

					if (!Array.isArray(oldData)) return data;

					oldData[index] = content;

					return {
						...data,
						[name]: oldData,
					};
				});
		};

		const hasDataChanges = useMemo(() => {
			const a = Object.entries(data).some(([name, value]) => {
				return block.metadata.data[name] !== value;
			});

			return a;
		}, [data, block.metadata.data]);

		const areDataValid = useMemo(() => {
			return Object.entries(data).every(([_name, value]) => {
				if (Array.isArray(value)) return true;
				const isAscii = /^[\x00-\x7F]*$/.test(value);
				return isAscii;
			});
		}, [data]);

		useEffect(() => {
			props.setCanSave(areDataValid);
			props.setHasChange(hasDataChanges);
		}, [hasDataChanges, areDataValid]);

		useImperativeHandle(
			ref,
			() => ({
				saveVariables: () => {
					Object.entries(data)
						.filter(([name, _]) =>
							props.config.data?.find((value) => name === value.size),
						)
						.forEach(([name, value]) => {
							dispatch(
								updateBlockDataValue({
									source,
									dataName: name,
									value,
								}),
							);

							if (name !== "image_size") {
								dispatch(
									updateBlockVariableContent({
										source,
										variableName: name,
										content: {
											type: "number",
											value: value.length,
										},
									}),
								);
							}
						});
				},
			}),
			[data, props, block],
		);

		if (!config.data || Object.keys(config.data).length === 0) {
			return null;
		}

		return (
			<>
				<div className={css.row}>
					<div className={css.sectionTitle}>Data</div>
					<div className={css.sectionTitle}></div>
				</div>
				{config.data.map((cfg, index) => {
					const currentData = data[cfg.size];

					const associatedSizeVariable = config.variables?.find(
						(c) => c.name === cfg.size,
					);

					if (associatedSizeVariable === undefined) {
						throw new Error(`Size variable "${cfg.size}" is not defined`);
					}

					if (associatedSizeVariable.content.type !== "number") {
						throw new Error(`Size variable "${cfg.size}" is not a number`);
					}

					const maximumSize = associatedSizeVariable.content.max;
					const tooltipText =
						typeof associatedSizeVariable.description == "string"
							? associatedSizeVariable.description
							: associatedSizeVariable.description[i18n.language] ||
								associatedSizeVariable.description["en"];

					if (cfg.multiple) {
						return languages?.map((language, index) => {
							const name = `${cfg.size}_${language.toLocaleLowerCase()}`;
							const multipleData = data[name] ?? "";

							if (typeof multipleData !== "string") {
								throw Error("invalid string value for multiple data");
							}

							const iconLanguageIndex = hasImage ? (
								<FontAwesomeIcon
									className={classNames(css.iconEye, {
										[css.iconEyeActive]: languageIndex == index,
									})}
									icon={faEye}
									onClick={() => setLanguageIndex(index)}
								/>
							) : null;

							return (
								<DataField
									key={`data-field-multiple-${index}`}
									index={index}
									tooltipText={tooltipText}
									content={multipleData}
									label={`${cfg.label} ${language}`}
									config={cfg}
									onChange={setDataContent(name)}
									onBlur={setDataParentContent(cfg.size, index)}
									maximumSize={maximumSize}
									iconLanguageIndex={iconLanguageIndex}
								/>
							);
						});
					}

					const message: string = Array.isArray(data[cfg.link])
						? data[cfg.link][languageIndex]
						: (data[cfg.link] as string);

					return cfg.type !== "file" ? (
						<DataField
							key={`data-field-${index}`}
							index={index}
							tooltipText={tooltipText}
							content={currentData}
							label={cfg.label}
							config={cfg}
							onChange={setDataContent(cfg.size)}
							maximumSize={maximumSize}
						/>
					) : (
						<DataImageField
							index={index}
							content={currentData}
							onChange={setDataImageContent(cfg.size)}
							maximumSize={maximumSize}
							block={block}
							tooltipText={tooltipText}
							message={message}
							config={props.config}
						/>
					);
				})}
			</>
		);
	},
);

export function BlockConfigWindow(
	props: BlockConfigWindowProps & { id: string },
) {
	switch (props.source.type) {
		case "operator":
			return <OperatorConfigWindow {...props.source} id={props.id} />;
		case "memoryRef":
			return <MemoryRefConfigWindow {...props.source} id={props.id} />;
	}
}

export function DataImageField({
	content,
	onChange,
	index,
	disabled,
	block,
	message,
	config,
}: DataImageFieldProps) {
	const { t } = useTranslation();

	const inputRef = useRef<HTMLInputElement>(null);
	const [preview, setPreview] = useState<{
		file: File | Blob | null;
		url: string;
	}>({ file: null, url: "" });
	const [image, setImage] = useState<{
		left: number;
		top: number;
		width: number;
		height: number;
	}>({ left: 0, top: 0, width: 0, height: 0 });

	useEffect(() => {
		setImage({
			left: Number(block?.metadata.variables?.start_x?.content?.value ?? 0) * 2,
			top: Number(block?.metadata.variables?.start_y?.content?.value ?? 0) * 2,
			width: 0,
			height: 0,
		});
		if (!content) return;

		const byteArray = fromHex(content);
		const blob = new Blob([byteArray as Uint8Array<ArrayBuffer>], {
			type: "image/bmp",
		});

		const img = URL.createObjectURL(blob);

		setPreview({ file: blob, url: img });
	}, []);

	const fileToHex = (file: File | Blob) => {
		file.arrayBuffer().then((arrayBuffer) => {
			const uint8Array = new Uint8Array(arrayBuffer);
			const uint8 = Array.from(uint8Array)
				.map((b) => b.toString(16).padStart(2, "0"))
				.join("");

			onChange({
				size: preview.file?.size ?? 0,
				data: uint8,
			});
		});
	};

	useEffect(() => {
		if (!preview.file) return;

		fileToHex(preview.file);
	}, [preview]);

	const onImageLoad = (e: SyntheticEvent<HTMLImageElement, Event>) => {
		const { naturalWidth, naturalHeight } = e.currentTarget;

		onChange({
			start_x: image.left / 2,
			start_y: image.top / 2,
			end_x: image.left / 2 + naturalWidth,
			end_y: image.top / 2 + naturalHeight,
		});
	};

	const handleClick = () => {
		if (inputRef.current && !preview.file) inputRef.current.click();
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();

		if (!event.dataTransfer.files || event.dataTransfer.files.length === 0)
			return;

		const file = event.dataTransfer.files[0];

		handleFileChange(file);
	};

	const handleFileClick = (event: ChangeEvent<HTMLInputElement>) => {
		if (!event.target.files || event.target.files.length === 0) return;

		const file = event.target.files[0];

		handleFileChange(file);
	};

	const getImageDimensions = (
		url: string,
	): Promise<{ width: number; height: number }> => {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.onload = () =>
				resolve({ width: img.naturalWidth, height: img.naturalHeight });
			img.onerror = (err) => reject(err);
			img.src = url;
		});
	};

	const handleFileChange = async (file: File) => {
		const validateSize = file.size + (config.size ?? 0) > IMAGE_MAX_SIZE;

		if (validateSize) {
			const diff = IMAGE_MAX_SIZE - (config.size ?? 0);
			toast.error(t("invalidImageSize", { size: diff }));
			return;
		}

		if (file && file.type.startsWith("image/bmp")) {
			const img = URL.createObjectURL(file);

			const dimensions: { width: number; height: number } =
				await getImageDimensions(img);

			if (
				dimensions.width * 2 > DEVICE_WIDTH ||
				dimensions.height * 2 > DEVICE_HEIGHT
			) {
				toast.error(
					t("invalidImageDimensions", {
						width: DEVICE_WIDTH / 2,
						height: DEVICE_HEIGHT / 2,
					}),
				);
				setPreview({ file: null, url: "" });
				return;
			}

			setPreview({ file, url: img });
		} else {
			setPreview({ file: null, url: "" });
			toast.error(t("invalidImageFormat", { format: ".bmp" }));
		}
	};

	const onImageMove = useDrag(
		{
			onMouseDown({ event }) {
				event.preventDefault();
			},
			onMouseMove({ event, delta }) {
				const target = event.target as HTMLElement;

				if (target.nodeName !== "IMG") return;

				setImage((position) => {
					const bounds = (event.target as HTMLElement).getBoundingClientRect();
					const imageWidth = bounds.width;
					const imageHeight = bounds.height;

					let newPositionTop = position.top + delta.y;
					let newPositionLeft = position.left + delta.x;

					if (newPositionTop < 0) newPositionTop = 0;
					if (newPositionTop + imageHeight > DEVICE_HEIGHT) {
						newPositionTop = DEVICE_HEIGHT - imageHeight;
					}

					if (newPositionLeft < 0) newPositionLeft = 0;
					if (newPositionLeft + imageWidth > DEVICE_WIDTH) {
						newPositionLeft = DEVICE_WIDTH - imageWidth;
					}

					return {
						left: newPositionLeft,
						top: newPositionTop,
						width: imageWidth,
						height: imageHeight,
					};
				});
			},
			onMouseUp({ event }) {
				event.stopPropagation();
				const file = event?.target as HTMLElement;

				if (file.nodeName !== "IMG") return;

				const bounds = file.getBoundingClientRect();
				const imageWidth = bounds.width / 2;
				const imageHeight = bounds.height / 2;
				const startY = Math.trunc(
					parseInt(file?.style.top.replace("px", "")) / 2,
				);
				const startX = Math.trunc(
					parseInt(file?.style.left.replace("px", "")) / 2,
				);

				onChange({
					start_x: startX,
					start_y: startY,
					end_x: startX + imageWidth,
					end_y: startY + imageHeight,
					size: preview.file?.size ?? 0,
				});
			},
			preventOnMouseDownIf: () => disabled ?? false,
		},
		[image, onChange],
	);

	const FormattedMessage = (text: string) => {
		return text.split("\\n").map((item, index) => (
			<Fragment key={index}>
				{item}
				<br />
			</Fragment>
		));
	};

	return (
		<div
			key={`data-field-${index}`}
			style={{
				width: DEVICE_WIDTH + 60,
				height: DEVICE_HEIGHT + 40,
				margin: "0 auto",
			}}
		>
			<input
				ref={inputRef}
				type="file"
				onChange={handleFileClick}
				accept=".bmp, image/bmp"
				style={{ display: "none" }}
			/>

			{preview.file && !disabled && (
				<button
					className={css.buttonRemoveImage}
					onClick={() => {
						setPreview({ file: null, url: "" });
						setImage({ top: 0, left: 0, width: 0, height: 0 });
						onChange({
							start_x: 0,
							start_y: 0,
							end_x: 0,
							end_y: 0,
							size: 0,
							data: "",
						});
						if (inputRef.current) {
							inputRef.current.value = "";
						}
					}}
				>
					<FontAwesomeIcon icon={faTrash} />
				</button>
			)}

			<Coordinates
				x={image.left}
				y={image.top}
				setY={(e) => {
					const newValue = parseInt(e.target.value || "0");

					if (newValue + image.height > DEVICE_HEIGHT) return;

					setImage({
						...image,
						top: newValue,
					});
				}}
				setX={(e) => {
					const newValue = parseInt(e.target.value || "0");

					if (newValue + image.width > DEVICE_WIDTH) return;

					setImage({
						...image,
						left: newValue,
					});
				}}
			/>

			<div
				style={{
					width: DEVICE_WIDTH,
					height: DEVICE_HEIGHT,
					border: "1px solid black",
					justifySelf: "center",
					cursor: "pointer",
					position: "relative",
				}}
				onClick={handleClick}
				onDragOver={(e) => {
					e.preventDefault();
				}}
				onDrop={handleDrop}
			>
				{message && (
					<span className={css.spanDisplay}>{FormattedMessage(message)}</span>
				)}

				{preview.file ? (
					<img
						onLoad={onImageLoad}
						onMouseDown={onImageMove}
						style={{
							position: "relative",
							display: "flex",
							left: image.left,
							top: image.top,
							transform: "scale(2)",
							transformOrigin: "top left",
						}}
						src={preview.url}
					/>
				) : (
					<span>{t("clickOrDropImage")}</span>
				)}
			</div>
		</div>
	);
}

export function DataField({
	content,
	config,
	onChange,
	index,
	tooltipText,
	label,
	onBlur,
	maximumSize,
	disabled,
	iconLanguageIndex,
}: DataFieldProps) {
	return (
		<div key={`data-field-${index}`} className={css.row}>
			<div className={css.label}>
				{label} {maximumSize ? `(Max: ${maximumSize})` : ""}
				<Tooltip text={tooltipText}>
					<FontAwesomeIcon color="#DC7609" size="xs" icon={faInfoCircle} />
				</Tooltip>
			</div>

			<div className={css.dataField}>
				{iconLanguageIndex && !disabled && iconLanguageIndex}
				<input
					type="text"
					value={content}
					maxLength={maximumSize}
					disabled={disabled}
					onBlur={(event) => {
						if (onBlur) {
							onBlur(event.target.value, config.multiple ?? false);
						}
					}}
					onChange={(event) => onChange(event.target.value)}
				/>
			</div>
		</div>
	);
}

export function BooleanVariable(props: {
	content: Types.Boolean;
	config?: VariableConfig;
	onChange: (value: Types.Boolean) => void;
}) {
	return (
		<div className={css.booleanVariableInput}>
			<input
				type="checkbox"
				checked={props.content.value}
				onChange={(event) => {
					props.onChange({
						...props.content,
						value: event.target.checked,
					});
				}}
			/>
		</div>
	);
}

export function EnumVariable(props: {
	content: Types.Enum;
	config: VariableConfig;
	onChange: (value: Types.Enum) => void;
}) {
	const content = props.config.content as TypesConfig.LogicsEnum;

	return (
		<div className={css.enumVariableInput}>
			<select
				value={props.content.value}
				onChange={(event) => {
					props.onChange({
						...props.content,
						value: event.target.value,
					});
				}}
			>
				{Object.keys(content.options).map((key) => {
					return (
						<option key={`enum-option-${key}`} value={key}>
							{key}
						</option>
					);
				})}
			</select>
		</div>
	);
}

export function NumberVariable(props: {
	content: Types.Hexadecimal;
	config: VariableConfig;
	onChange: (value: Types.Hexadecimal) => void;
	readOnly?: boolean;
}) {
	const { t } = useTranslation();
	const [error, setError] = useState<string | null>(null);
	const content = props.config.content as TypesConfig.LogicsNumber;
	const min = content.min || 0;

	return (
		<div className={css.numberVariableInput}>
			<input
				className={css.numberVariableInput}
				type="text"
				value={props.content.value}
				readOnly={props.readOnly}
				onChange={(event) => {
					props.onChange({
						...props.content,
						value: event.target.value,
					});
				}}
				onBlur={(event) => {
					setError(null);
					let newValue = null;

					if (event.target.value.startsWith("0x")) {
						newValue = parseInt(event.target.value, 16);
						if (isNaN(newValue)) {
							setError(t("hex"));
							newValue = 0;
						}
					} else {
						newValue = parseInt(event.target.value.replace(/\D/g, ""));
					}

					if (newValue < min) {
						setError(t("min", { min: min }));
						newValue = min;
					}

					if (content?.max && newValue > content.max) {
						setError(t("max", { max: content.max }));
						newValue = content.max;
					}

					props.onChange({
						...props.content,
						value: newValue,
					});
				}}
			/>
			{error && <label className={css.errorVariable}>{error}</label>}
		</div>
	);
}
